import React, { useState, useMemo } from 'react';
import { Icons } from '../ui/Icons';
import type { ParkingReservation, ReservationSource } from '../../types';
import Modal from '../ui/Modal';
import ConfirmationModal from '../ui/ConfirmationModal';

interface ParkingReservationViewProps {
    reservations: ParkingReservation[];
    reservationSources: ReservationSource[];
    onSaveReservation: (reservation: Omit<ParkingReservation, 'id'> & { id?: string }) => void;
    onDeleteReservation: (id: string) => void;
    onInitiatePrint: (reservation: ParkingReservation) => void;
}

const ParkingReservationView: React.FC<ParkingReservationViewProps> = ({
    reservations,
    reservationSources,
    onSaveReservation,
    onDeleteReservation,
    onInitiatePrint
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showInactive, setShowInactive] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingReservation, setEditingReservation] = useState<ParkingReservation | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [reservationToDelete, setReservationToDelete] = useState<string | null>(null);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewReservation, setPreviewReservation] = useState<ParkingReservation | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    const [formData, setFormData] = useState<Partial<ParkingReservation>>({
        reservationSource: '',
        confirmationNumber: '',
        numberOfPeople: '',
        authorizedBy: '',
        licensePlate: '',
        guestName: '',
        roomNumber: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        status: 'Reservation',
        notes: ''
    });

    const isReservationInactive = (res: ParkingReservation) => {
        if (res.status === 'Checkout') return true;
        
        const endDate = new Date(res.endDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        
        const diffTime = today.getTime() - endDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        return diffDays > 2;
    };

    const isReservationLate = (res: ParkingReservation) => {
        if (res.status === 'Checkout') return false;
        
        const endDate = new Date(res.endDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        
        return today.getTime() > endDate.getTime();
    };

    const isReservationExpiringToday = (res: ParkingReservation) => {
        if (res.status === 'Checkout') return false;
        
        const endDate = new Date(res.endDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        
        return today.getTime() === endDate.getTime();
    };

    const filteredReservations = useMemo(() => {
        return reservations.filter(res => {
            const inactive = isReservationInactive(res);
            // Status filter
            if (!showInactive && inactive) {
                return false;
            }

            const searchLower = searchTerm.toLowerCase();
            return (
                (res.reservationSource || '').toLowerCase().includes(searchLower) ||
                (res.confirmationNumber || '').toLowerCase().includes(searchLower) ||
                (res.authorizedBy || '').toLowerCase().includes(searchLower) ||
                (res.licensePlate || '').toLowerCase().includes(searchLower) ||
                (res.guestName || '').toLowerCase().includes(searchLower) ||
                (res.roomNumber || '').toLowerCase().includes(searchLower)
            );
        }).sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
    }, [reservations, searchTerm, showInactive]);

    const totalPages = Math.ceil(filteredReservations.length / ITEMS_PER_PAGE);
    const paginatedReservations = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredReservations.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredReservations, currentPage]);

    // Reset to first page when search or filters change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, showInactive]);

    const handleOpenPreview = (reservation: ParkingReservation) => {
        setPreviewReservation(reservation);
        setIsPreviewModalOpen(true);
    };

    const handleUpdateStatus = (reservation: ParkingReservation, newStatus: ParkingReservation['status']) => {
        onSaveReservation({
            ...reservation,
            status: newStatus
        });
        if (previewReservation?.id === reservation.id) {
            setPreviewReservation({ ...reservation, status: newStatus });
        }
    };

    const handleOpenModal = (reservation?: ParkingReservation) => {
        if (reservation) {
            setEditingReservation(reservation);
            setFormData(reservation);
        } else {
            setEditingReservation(null);
            setFormData({
                reservationSource: '',
                confirmationNumber: '',
                numberOfPeople: '',
                authorizedBy: '',
                licensePlate: '',
                guestName: '',
                roomNumber: '',
                startDate: new Date().toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0],
                status: 'Reservation',
                notes: ''
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingReservation(null);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSaveReservation({
            ...formData,
            id: editingReservation?.id,
            createdAt: editingReservation?.createdAt || new Date().toISOString()
        } as any);
        handleCloseModal();
    };

    const confirmDelete = (id: string) => {
        setReservationToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const handleDelete = () => {
        if (reservationToDelete) {
            onDeleteReservation(reservationToDelete);
            setIsDeleteModalOpen(false);
            setReservationToDelete(null);
        }
    };

    return (
        <div className="space-y-6 max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Icons.Car className="w-6 h-6 text-indigo-600" />
                        Parking Reservations
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">Manage guest parking spots and vehicle information</p>
                </div>
                <button
                    onClick={() => handleOpenModal()}
                    className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors shadow-sm"
                >
                    <Icons.Plus className="w-4 h-4 mr-2" />
                    New Reservation
                </button>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-slate-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="relative max-w-md w-full">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Icons.Search className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search by spot, plate, name, or room..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md leading-5 bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Show Inactive</span>
                        <button
                            onClick={() => setShowInactive(!showInactive)}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${showInactive ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-slate-700'}`}
                        >
                            <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${showInactive ? 'translate-x-5' : 'translate-x-0'}`}
                            />
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                        <thead className="bg-gray-50 dark:bg-slate-800/50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Source & Conf #</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Guest Info</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Vehicle</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Details</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dates</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                <th scope="col" className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                            {paginatedReservations.length > 0 ? (
                                paginatedReservations.map((res) => {
                                    const isLate = isReservationLate(res);
                                    const isExpiringToday = isReservationExpiringToday(res);
                                    const isInactive = isReservationInactive(res);
                                    return (
                                        <tr key={res.id} className={`hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors ${isLate ? 'bg-red-50 dark:bg-red-900/10' : isExpiringToday ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''} ${isInactive ? 'opacity-60 grayscale-[0.3]' : ''}`}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    {isLate && <Icons.AlertTriangle className="w-4 h-4 text-red-600 animate-pulse" />}
                                                    {isExpiringToday && <Icons.Clock className="w-4 h-4 text-yellow-600" />}
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white">{res.reservationSource}</div>
                                                        <div className="text-sm text-gray-500 dark:text-gray-400">#{res.confirmationNumber}</div>
                                                    </div>
                                                </div>
                                            </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-white font-medium">{res.guestName}</div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">Room {res.roomNumber}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-200 border border-gray-200 dark:border-slate-600">
                                                {res.licensePlate}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-white"><Icons.Users className="w-4 h-4 inline mr-1" /> {res.numberOfPeople}</div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">Auth: {res.authorizedBy}</div>
                                        </td>
                                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${isLate ? 'text-red-600 dark:text-red-400 font-bold' : isExpiringToday ? 'text-yellow-600 dark:text-yellow-500 font-bold' : 'text-gray-500 dark:text-gray-400'}`}>
                                            <div>{res.startDate}</div>
                                            <div>to {res.endDate}</div>
                                            {isExpiringToday && (
                                                <div className="mt-1">
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 uppercase tracking-wider">
                                                        Due Today
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                                                ${res.status === 'Check-in' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 
                                                  res.status === 'Checkout' ? 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-300' : 
                                                  'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'}`}>
                                                {res.status}
                                            </span>
                                            {isLate && (
                                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-600 text-white animate-pulse shadow-sm">
                                                    LATE
                                                </span>
                                            )}
                                            {isReservationInactive(res) && !isLate && (
                                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                                    Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end gap-2">
                                                <button onClick={() => handleOpenPreview(res)} className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200" title="Preview">
                                                    <Icons.Eye className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => onInitiatePrint(res)} className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200" title="Print Voucher">
                                                    <Icons.Printer className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => handleOpenModal(res)} className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300" title="Edit">
                                                    <Icons.Edit className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => confirmDelete(res.id)} className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300" title="Delete">
                                                    <Icons.Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                            ) : (
                                <tr>
                                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                                        No parking reservations found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="px-6 py-4 bg-gray-50 dark:bg-slate-800/50 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-slate-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-slate-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                    Showing <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, filteredReservations.length)}</span> of <span className="font-medium">{filteredReservations.length}</span> results
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
                                    >
                                        <span className="sr-only">Previous</span>
                                        <Icons.ChevronLeft className="h-5 w-5" />
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                currentPage === page
                                                    ? 'z-10 bg-indigo-50 dark:bg-indigo-900/20 border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                                    : 'bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-600'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
                                    >
                                        <span className="sr-only">Next</span>
                                        <Icons.ChevronRight className="h-5 w-5" />
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {isModalOpen && (
                <Modal onClose={handleCloseModal} title={editingReservation ? "Edit Reservation" : "New Parking Reservation"} maxWidthClass="sm:max-w-6xl">
                    <form onSubmit={handleSubmit} className="space-y-6 p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reservation Source *</label>
                            <select
                                required
                                value={formData.reservationSource}
                                onChange={(e) => setFormData({ ...formData, reservationSource: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-700 dark:text-white sm:text-sm"
                            >
                                <option value="">Select a source</option>
                                {reservationSources.map(source => (
                                    <option key={source.id} value={source.name}>{source.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirmation Number *</label>
                            <input
                                type="text"
                                required
                                value={formData.confirmationNumber}
                                onChange={(e) => setFormData({ ...formData, confirmationNumber: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-700 dark:text-white sm:text-sm"
                                placeholder="e.g., 12345678"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Guest Name *</label>
                            <input
                                type="text"
                                required
                                value={formData.guestName}
                                onChange={(e) => setFormData({ ...formData, guestName: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-700 dark:text-white sm:text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Number of People *</label>
                            <input
                                type="number"
                                required
                                min="1"
                                value={formData.numberOfPeople}
                                onChange={(e) => setFormData({ ...formData, numberOfPeople: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-700 dark:text-white sm:text-sm"
                                placeholder="e.g., 2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Room Number *</label>
                            <input
                                type="text"
                                required
                                value={formData.roomNumber}
                                onChange={(e) => setFormData({ ...formData, roomNumber: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-700 dark:text-white sm:text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">License Plate *</label>
                            <input
                                type="text"
                                required
                                value={formData.licensePlate}
                                onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-700 dark:text-white sm:text-sm uppercase"
                                placeholder="ABC-1234"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date *</label>
                            <input
                                type="date"
                                required
                                value={formData.startDate}
                                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-700 dark:text-white sm:text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date *</label>
                            <input
                                type="date"
                                required
                                value={formData.endDate}
                                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-700 dark:text-white sm:text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Authorized By *</label>
                            <input
                                type="text"
                                required
                                value={formData.authorizedBy}
                                onChange={(e) => setFormData({ ...formData, authorizedBy: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-700 dark:text-white sm:text-sm"
                                placeholder="e.g., John Doe"
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-700 dark:text-white sm:text-sm"
                            >
                                <option value="Reservation">Reservation</option>
                                <option value="Check-in">Check-in</option>
                                <option value="Checkout">Checkout</option>
                            </select>
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-700 dark:text-white sm:text-sm"
                                placeholder="Any additional information..."
                            />
                        </div>
                    </div>
                    
                    <div className="mt-6 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={handleCloseModal}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-slate-700 dark:text-gray-200 dark:border-slate-600 dark:hover:bg-slate-600"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            {editingReservation ? 'Save Changes' : 'Create Reservation'}
                        </button>
                    </div>
                </form>
                </Modal>
            )}

            {isPreviewModalOpen && previewReservation && (
                <Modal onClose={() => setIsPreviewModalOpen(false)} title="Reservation Details" maxWidthClass="sm:max-w-2xl">
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Guest Name</h4>
                                <p className="mt-1 text-sm text-gray-900 dark:text-white font-medium">{previewReservation.guestName}</p>
                            </div>
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Room Number</h4>
                                <p className="mt-1 text-sm text-gray-900 dark:text-white font-medium">{previewReservation.roomNumber}</p>
                            </div>
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">License Plate</h4>
                                <p className="mt-1 text-sm text-gray-900 dark:text-white font-medium">{previewReservation.licensePlate}</p>
                            </div>
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Confirmation #</h4>
                                <p className="mt-1 text-sm text-gray-900 dark:text-white font-medium">#{previewReservation.confirmationNumber}</p>
                            </div>
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Dates</h4>
                                <p className="mt-1 text-sm text-gray-900 dark:text-white font-medium">{previewReservation.startDate} to {previewReservation.endDate}</p>
                            </div>
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Source</h4>
                                <p className="mt-1 text-sm text-gray-900 dark:text-white font-medium">{previewReservation.reservationSource}</p>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Update Status</label>
                            <div className="flex gap-2">
                                {['Reservation', 'Check-in', 'Checkout'].map((status) => (
                                    <button
                                        key={status}
                                        onClick={() => handleUpdateStatus(previewReservation, status as any)}
                                        className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                                            previewReservation.status === status
                                                ? 'bg-indigo-600 text-white border-indigo-600'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-slate-700 dark:text-gray-200 dark:border-slate-600 dark:hover:bg-slate-600'
                                        }`}
                                    >
                                        {status}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {previewReservation.notes && (
                            <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Notes</h4>
                                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{previewReservation.notes}</p>
                            </div>
                        )}

                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setIsPreviewModalOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 dark:bg-slate-700 dark:text-gray-200 dark:border-slate-600"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {isDeleteModalOpen && (
                <ConfirmationModal
                    title="Delete Reservation"
                    message="Are you sure you want to delete this parking reservation? This action cannot be undone."
                    confirmButtonText="Delete"
                    onConfirm={handleDelete}
                    onCancel={() => {
                        setIsDeleteModalOpen(false);
                        setReservationToDelete(null);
                    }}
                />
            )}
        </div>
    );
};

export default ParkingReservationView;
